import { useCallback, useState } from 'react';
import type { Project } from '../../domain/schemas';
import { useVisualHistory } from '../../state/historyContext';
import { useProject } from '../../state/projectContext';

/**
 * Runs a pure scene/character operation as one transaction on the open project. `op`
 * receives the project exactly as the session is about to replace it, and its result is
 * what gets applied, so it can never overwrite a newer project with a stale one.
 *
 * - **Abort:** to reject an edit, `op` throws. Nothing is written, no undo step is
 *   recorded, no save is scheduled, `edit` returns false, and the message becomes `error`.
 *   Operation errors (locked objects, limits, invalid values) work the same way.
 * - **No-op:** returning the same project is a successful no-op.
 * - **Labels:** `label` names the undo step; `coalesceKey` merges rapid edits of the same
 *   field (typing in a number box) into one undo step. The label is set only once `op`
 *   has produced a change, so a rejected edit never labels a later one.
 */
export type EditFn = (label: string, op: (p: Project) => Project, coalesceKey?: string) => boolean;

export function useSceneEdit(): { edit: EditFn; error: string | null; setError: (message: string | null) => void } {
  const { session, update } = useProject();
  const history = useVisualHistory();
  const [error, setError] = useState<string | null>(null);
  const edit = useCallback(
    (label: string, op: (p: Project) => Project, coalesceKey?: string) => {
      if (!session.snapshot.project) return false;
      try {
        // ProjectSession.update calls this with its current project before changing any
        // state; a throw leaves the session untouched. The history hint is set just before
        // the change is published, so VisualHistory records exactly one labelled step.
        update((current) => {
          const next = op(current);
          if (next !== current) history.hint(label, coalesceKey ?? null);
          return next;
        });
        setError(null);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'That change could not be applied.');
        return false;
      }
    },
    [history, session, update],
  );
  return { edit, error, setError };
}
